import React, { useCallback, useState, useRef, Dispatch } from "react";
import {
  Box,
  Button,
  IconButton,
  Image,
  Input,
  Text,
  Flex,
  Heading,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { Tooltip } from "../ui/tooltip";
import useToaster from "../../hooks/useToaster";
import { ApiResponse } from "../../services/Dtos";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import useIcons from "../../hooks/useIcons";
import useThemeColors from "../../hooks/useThemeColors";

interface Props {
  image: string | null;
  handleUpload: () => Promise<ApiResponse | null>;
  handleDelete: () => Promise<ApiResponse | null>;
  successMessage: string;
  errorMessage: string;
  heading: string;
  body: string;
  buttonText: string;
  media: string | null;
  setMedia: Dispatch<React.SetStateAction<string | null>>;
}

export function ImageUpload({
  image,
  handleUpload,
  handleDelete,
  heading,
  body,
  buttonText,
  media,
  setMedia,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const { showSuccessToast, showErrorToast } = useToaster();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { accentColor } = useSchoolsContext();
  const icons = useIcons();
  const colors = useThemeColors();

  const onFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];

      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          setMedia(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    []
  );

  const handleUploadClick = async () => {
    if (media) {
      setLoading(true);
      const response = await handleUpload();

      if (response?.succeeded) {
        showSuccessToast(response.message || "");
      } else {
        showErrorToast(response?.message || "");
      }
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    if (media) {
      setMedia(null); // Remove the preview image and show the image from the database
    } else if (image) {
      setLoading(true);
      const response = await handleDelete(); // Send a request to delete the image from the server

      if (response?.succeeded) {
        showSuccessToast(response.message || "");
      } else {
        showErrorToast(response?.message || "");
      }
      setLoading(false);
    }
  };

  const hasImage = media || image;

  return (
    <Box
      bg={colors.background.card}
      p={4}
      borderRadius="lg"
      borderWidth={1}
      borderColor={colors.border.primary}
    >
      <VStack gap={4} align="start">
        {/* Header Section */}
        <VStack align="start" gap={1}>
          <Heading size="sm">{heading}</Heading>
          <Text fontSize="sm" opacity={0.8}>
            {body}
          </Text>
        </VStack>

        {/* Image Section */}
        <Box>
          {hasImage ? (
            <Box position="relative" display="inline-block">
              <Image
                bg="gray.100"
                src={media ?? image ?? undefined}
                alt={heading}
                boxSize="160px"
                aspectRatio={1}
                objectFit="cover"
                borderRadius="xl"
                borderWidth={2}
                borderColor={colors.border.primary}
              />

              {/* Action Buttons Overlay */}
              <HStack
                position="absolute"
                top={2}
                right={2}
                gap={1}
                _groupHover={{ opacity: 1 }}
                transition="opacity 0.2s"
              >
                <Tooltip
                  content="Change image"
                  positioning={{ placement: "top" }}
                >
                  <IconButton
                    aria-label="Change image"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    colorPalette={accentColor}
                    variant="solid"
                    _hover={{
                      transform: "scale(1.05)",
                    }}
                    transition="all 0.2s"
                  >
                    <Icon as={icons.utility.edit} />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  content="Remove image"
                  positioning={{ placement: "top" }}
                >
                  <IconButton
                    aria-label="Remove image"
                    size="sm"
                    onClick={handleDeleteClick}
                    colorPalette="red"
                    variant="solid"
                    _hover={{
                      transform: "scale(1.05)",
                    }}
                    transition="all 0.2s"
                  >
                    <Icon as={icons.utility.delete} />
                  </IconButton>
                </Tooltip>
              </HStack>
            </Box>
          ) : (
            <Box
              boxSize="160px"
              borderRadius="xl"
              borderWidth={2}
              borderColor={colors.border.primary}
              borderStyle="dashed"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={() => fileInputRef.current?.click()}
              _hover={{
                borderColor: `${accentColor}.400`,
                bg: colors.background.tertiary,
              }}
              transition="all 0.2s"
              role="group"
            >
              <Icon
                as={icons.utility.plus}
                boxSize={8}
                opacity={0.6}
                _groupHover={{ opacity: 1 }}
              />
              <Text
                fontSize="sm"
                opacity={0.6}
                mt={2}
                _groupHover={{ opacity: 1 }}
              >
                Click to upload
              </Text>
              <Text fontSize="xs" opacity={0.4} mt={1}>
                JPG, PNG
              </Text>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Flex
          gap={3}
          w={"100%"}
          flexDir={["column"]}
          align={["stretch", "flex-start"]}
        >
          <Button
            colorPalette={accentColor}
            variant="outline"
            loading={loading}
            onClick={handleUploadClick}
            disabled={!media}
          >
            <Icon as={icons.utility.check} mr={2} />
            {buttonText || "Save Image"}
          </Button>
          {media && !image && (
            <Button variant="ghost" onClick={() => setMedia(null)}>
              Cancel
            </Button>
          )}
        </Flex>
      </VStack>

      {/* Hidden File Input */}
      <Input
        ref={fileInputRef}
        id="fileInput"
        type="file"
        accept=".jpg,.jpeg,.png"
        onChange={onFileChange}
        style={{ display: "none" }}
      />
    </Box>
  );
}
